import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Mic,
  MicOff,
  Maximize2,
  Minimize2,
  Monitor,
  MonitorOff,
  Phone,
  PhoneOff,
  Video,
  VideoOff,
} from 'lucide-react';
import type { CallParticipant } from '@homework/shared';
import { formatDuration } from '@homework/shared';
import { useMe } from '../lib/auth';
import { useCalls } from '../lib/calls';
import { Avatar, cx } from './ui';

/**
 * The in-call surface (FR-6.4 – FR-6.8): the incoming-call prompt, and a small
 * floating panel while a call is running.
 *
 * Deliberately not full screen — someone on a call is still working, so the
 * panel stays in a corner, can be collapsed to a pill, and never traps focus.
 * Escape collapses it; it never hangs up, because silently dropping a live call
 * on a stray keypress is the worst thing this component could do.
 */
export function CallOverlay() {
  const { activeCall, incoming } = useCalls();

  return (
    <>
      {incoming ? <IncomingCallPrompt /> : null}
      {activeCall ? <ActiveCallPanel /> : null}
    </>
  );
}

// ------------------------------------------------------------------ incoming

function IncomingCallPrompt() {
  const { incoming, acceptIncoming, rejectIncoming } = useCalls();
  const answerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    answerRef.current?.focus();
  }, []);

  if (!incoming) return null;

  return (
    <div
      role="alertdialog"
      aria-live="assertive"
      aria-label={`Incoming call from ${incoming.from.name}`}
      className="glass-strong fixed right-4 top-4 z-50 w-[min(22rem,calc(100vw-2rem))] animate-slide-up rounded-3xl p-4 ring-1 ring-inset ring-brand-400/50"
    >
      <div className="flex items-start gap-3">
        <span className="relative">
          <Avatar name={incoming.from.name} photoUrl={incoming.from.photoUrl} seed={incoming.from.userId} size="lg" />
          <span className="absolute inset-0 animate-pulse-ring rounded-full border-2 border-brand-400" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-slate-900 dark:text-slate-50">{incoming.from.name}</p>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {incoming.audioOnly ? 'Incoming voice call' : 'Incoming video call'}
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-2">
        <button
          ref={answerRef}
          type="button"
          className="btn-primary w-full"
          onClick={() => void acceptIncoming({ audioOnly: false })}
        >
          <Video className="h-4 w-4" aria-hidden />
          Answer with video
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-secondary flex-1"
            onClick={() => void acceptIncoming({ audioOnly: true })}
          >
            <Phone className="h-4 w-4" aria-hidden />
            Audio only
          </button>
          <button
            type="button"
            className="btn-danger flex-1"
            onClick={() => rejectIncoming('Declined')}
          >
            <PhoneOff className="h-4 w-4" aria-hidden />
            Decline
          </button>
        </div>
      </div>
    </div>
  );
}

/** Remote tiles drawn at once; the rest of the room is counted instead. */
const MAX_TILES = 8;

// -------------------------------------------------------------------- in call

function ActiveCallPanel() {
  const {
    activeCall,
    localStream,
    remoteStreams,
    connecting,
    muted,
    cameraOn,
    screenSharing,
    sharingUserIds,
    leaveCall,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
  } = useCalls();
  const me = useMe();
  const [collapsed, setCollapsed] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const startedAt = activeCall?.startedAt ?? null;
  useEffect(() => {
    if (!startedAt) return;
    const started = new Date(startedAt).getTime();
    const tick = () => setSeconds(Math.max(0, Math.round((Date.now() - started) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  // Escape gets you out of the panel, never out of the call.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setCollapsed(true);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const others = useMemo<CallParticipant[]>(
    () => (activeCall?.participants ?? []).filter((participant) => participant.userId !== me.user.id),
    [activeCall, me.user.id],
  );

  /**
   * A room can hold a hundred people, and a hundred tiles in a panel this size
   * shows nobody. Whoever has their camera on is who you are looking at, so
   * they come first, and the rest are counted.
   */
  const visible = useMemo<CallParticipant[]>(() => {
    if (others.length <= MAX_TILES) return others;
    const withVideo = others.filter((participant) => !participant.audioOnly);
    const withoutVideo = others.filter((participant) => participant.audioOnly);
    return [...withVideo, ...withoutVideo].slice(0, MAX_TILES);
  }, [others]);
  const hidden = others.length - visible.length;

  /**
   * A shared screen is the thing everyone is looking at, so it gets the panel
   * rather than a half-width thumbnail of it. Unreadable at tile size was the
   * whole complaint: a screen in a 10rem box is a picture of a screen.
   */
  const remoteSharer = others.find((participant) => sharingUserIds.has(participant.userId)) ?? null;
  const spotlight = screenSharing
    ? { stream: localStream, label: 'Your screen', name: me.user.name, photoUrl: me.user.photoUrl }
    : remoteSharer
      ? {
          stream: remoteStreams.get(remoteSharer.userId) ?? null,
          label: `${remoteSharer.name}'s screen`,
          name: remoteSharer.name,
          photoUrl: remoteSharer.photoUrl,
        }
      : null;

  if (!activeCall) return null;

  const title =
    activeCall.kind === 'ROOM'
      ? `Meeting room${activeCall.roomId ? ` · ${activeCall.roomId}` : ''}`
      : others[0]?.name ?? 'Calling…';

  if (collapsed) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          type="button"
          className="btn-primary"
          onClick={() => setCollapsed(false)}
          aria-label={`Return to the call with ${title}, ${formatDuration(seconds)} elapsed`}
        >
          <Phone className="h-4 w-4" aria-hidden />
          <span className="max-w-[10rem] truncate">{title}</span>
          <span className="tabular-nums opacity-80">{formatDuration(seconds)}</span>
        </button>
      </div>
    );
  }

  return (
    <section
      aria-label={`Call with ${title}`}
      className="glass-strong fixed bottom-4 right-4 z-50 w-[min(22rem,calc(100vw-2rem))] animate-slide-up overflow-hidden rounded-3xl"
    >
      <header className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2 dark:border-slate-800">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-50">{title}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {connecting ? (
              'Connecting…'
            ) : (
              <>
                <span className="tabular-nums">{formatDuration(seconds)}</span>
                <span aria-hidden> · </span>
                <span>
                  {others.length + 1} {others.length + 1 === 1 ? 'person' : 'people'}
                </span>
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          className="btn-ghost p-2"
          onClick={() => setCollapsed(true)}
          aria-label="Collapse the call panel"
        >
          <Minimize2 className="h-4 w-4" aria-hidden />
        </button>
      </header>

      {spotlight ? (
        <div className="bg-slate-950 p-1.5">
          <MediaTile
            stream={spotlight.stream}
            label={spotlight.label}
            name={spotlight.name}
            photoUrl={spotlight.photoUrl}
            muted
            // Never cropped: a shared screen loses its edges under object-cover,
            // and the edges are usually where the toolbar is.
            fit="contain"
            allowFullscreen
          />
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-1.5 bg-slate-100 p-1.5 dark:bg-slate-950">
        {spotlight && screenSharing ? null : (
          <MediaTile
            stream={localStream}
            label="You"
            name={me.user.name}
            photoUrl={me.user.photoUrl}
            muted
            videoOff={!cameraOn && !screenSharing}
          />
        )}
        {others.length === 0 ? (
          <div className="flex aspect-video items-center justify-center rounded-lg bg-slate-200 p-2 text-center text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {activeCall.kind === 'ROOM' ? 'Waiting for someone to walk in' : 'Waiting for them to answer'}
          </div>
        ) : (
          <>
            {visible.map((participant) => (
              <MediaTile
                key={participant.userId}
                stream={remoteStreams.get(participant.userId) ?? null}
                label={participant.name}
                name={participant.name}
                photoUrl={participant.photoUrl}
                videoOff={participant.audioOnly}
                // Their voice plays from the call provider, which outlives this
                // tile; leaving it on here would play everyone twice.
                muted
              />
            ))}
            {hidden > 0 ? (
              <div className="flex aspect-video items-center justify-center rounded-lg bg-slate-200 p-2 text-center text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                +{hidden} more in the room
              </div>
            ) : null}
          </>
        )}
      </div>

      <div className="flex items-center justify-between gap-1.5 px-3 py-2.5">
        <div className="flex gap-1.5">
          <ControlButton
            label={muted ? 'Unmute microphone' : 'Mute microphone'}
            active={muted}
            onClick={toggleMute}
            icon={muted ? MicOff : Mic}
          />
          <ControlButton
            label={cameraOn ? 'Turn camera off' : 'Turn camera on'}
            active={!cameraOn}
            onClick={() => void toggleCamera()}
            icon={cameraOn ? Video : VideoOff}
          />
          <ControlButton
            label={screenSharing ? 'Stop sharing your screen' : 'Share your screen'}
            active={screenSharing}
            onClick={() => void toggleScreenShare()}
            icon={screenSharing ? MonitorOff : Monitor}
          />
        </div>
        <button type="button" className="btn-danger px-3 py-1.5 text-xs" onClick={leaveCall}>
          <PhoneOff className="h-3.5 w-3.5" aria-hidden />
          Leave
        </button>
      </div>
    </section>
  );
}

function ControlButton({
  label,
  active,
  onClick,
  icon: Icon,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  icon: typeof Mic;
}) {
  return (
    <button
      type="button"
      className={cx(
        'btn px-2.5 py-1.5',
        active
          ? 'bg-rose-100 text-rose-700 hover:bg-rose-200 dark:bg-rose-500/20 dark:text-rose-300 dark:hover:bg-rose-500/30'
          : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800',
      )}
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      title={label}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}

/** One video tile; falls back to the avatar whenever there is no live video. */
function MediaTile({
  stream,
  label,
  name,
  photoUrl,
  muted = false,
  videoOff = false,
  fit = 'cover',
  allowFullscreen = false,
}: {
  stream: MediaStream | null;
  label: string;
  name: string;
  photoUrl?: string | null;
  muted?: boolean;
  videoOff?: boolean;
  /** A shared screen must not be cropped; a face is better filling its tile. */
  fit?: 'cover' | 'contain';
  allowFullscreen?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasVideo, setHasVideo] = useState(false);

  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;
    element.srcObject = stream;
    if (stream) void element.play().catch(() => undefined);

    const sync = () => setHasVideo((stream?.getVideoTracks().length ?? 0) > 0);
    sync();
    stream?.addEventListener('addtrack', sync);
    stream?.addEventListener('removetrack', sync);
    return () => {
      stream?.removeEventListener('addtrack', sync);
      stream?.removeEventListener('removetrack', sync);
      element.srcObject = null;
    };
  }, [stream]);

  const showVideo = hasVideo && !videoOff;

  return (
    <div className="relative aspect-video overflow-hidden rounded-lg bg-slate-800">
      <video
        ref={videoRef}
        muted={muted}
        autoPlay
        playsInline
        aria-label={`${label} video`}
        className={cx(
          'h-full w-full',
          fit === 'contain' ? 'object-contain' : 'object-cover',
          showVideo ? 'block' : 'hidden',
        )}
      />
      {allowFullscreen && showVideo ? (
        <button
          type="button"
          className="glass-strong absolute right-1.5 top-1.5 rounded-full p-1.5 text-slate-700"
          aria-label="Show this full screen"
          onClick={() => void videoRef.current?.requestFullscreen?.().catch(() => undefined)}
        >
          <Maximize2 className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
      {showVideo ? null : (
        <div className="flex h-full w-full items-center justify-center bg-slate-200 dark:bg-slate-800">
          <Avatar name={name} photoUrl={photoUrl} size="md" />
        </div>
      )}
      <span className="absolute bottom-1 left-1 max-w-[90%] truncate rounded bg-slate-900/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
        {label}
      </span>
    </div>
  );
}
